import { useMemo } from 'react';
import { alpha, Stack, Typography, useTheme } from '@mui/material';
import dayjs from 'dayjs';

// Components
import { StyledWhiteBox } from 'src/features/PortDetail/config';
import AreaChart from 'src/components/HighChart/AreaChart';

// Utilities
import { useQueryDataLoadDischargeChart } from 'src/services/queries';
import { FORMAT_DATE_TIME } from 'src/utils/constants/config';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import { IContainerTradeChart } from 'src/utils/typings/response';
import { renderPriceOrDefault } from 'src/utils/helpers/stringHelpers';

interface IProps {
  locode: string;
}

const LoadDischargeChart = ({ locode = '' }: IProps) => {
  const theme = useTheme();
  const { data, isFetching } = useQueryDataLoadDischargeChart({ locode });
  const dataLoadDischargeChart = getArrayDefault(data?.data);

  const lineChartYData = useMemo(() => {
    const importData: Array<number> = [];
    const exportData: Array<number> = [];
    const transshipmentData: Array<number> = [];
    dataLoadDischargeChart.map((item: IContainerTradeChart) => {
      importData.push(item.imports);
      exportData.push(item.exports);
      transshipmentData.push(item.transshipments);
    });
    return { importData: importData, exportData: exportData, transshipmentData: transshipmentData };
  }, [dataLoadDischargeChart]);

  const lineChartXData = useMemo(() => {
    const res = dataLoadDischargeChart.map((item: IContainerTradeChart) => {
      return dayjs(item?.date, FORMAT_DATE_TIME.yyyy_mm_dd).format(FORMAT_DATE_TIME.FULL_ONLY_DATE);
    });
    return res;
  }, [dataLoadDischargeChart]);

  return (
    <StyledWhiteBox flex={1}>
      <Stack mb={3} spacing={3} direction={'row'} alignItems={'center'} justifyContent="space-between">
        <Typography variant="h5">Days between load/discharge and terminal gate</Typography>
      </Stack>
      <AreaChart
        loading={isFetching}
        series={[
          {
            name: 'Export',
            type: 'area',
            lineColor: '#FF718B',
            fillColor: {
              linearGradient: { x1: 0, y1: 1, x2: 0, y2: 0 },
              stops: [
                [0, alpha('#FF718B', 0.05)],
                [1, alpha('#FF718B', 0.2)],
              ],
            },
            data: lineChartYData.exportData,
          },
          {
            name: 'Import',
            type: 'area',
            lineColor: theme.palette.secondary.main,
            fillColor: {
              linearGradient: { x1: 0, y1: 1, x2: 0, y2: 0 },
              stops: [
                [0, alpha(theme.palette.secondary.main, 0.05)],
                [1, alpha(theme.palette.secondary.main, 0.2)],
              ],
            },
            data: lineChartYData.importData,
          },
          {
            name: 'Transshipment',
            type: 'area',
            lineColor: '#93AAFD',
            fillColor: {
              linearGradient: { x1: 0, y1: 1, x2: 0, y2: 0 },
              stops: [
                [0, alpha('#93AAFD', 0.05)],
                [1, alpha('#93AAFD', 0.2)],
              ],
            },
            data: lineChartYData.transshipmentData,
          },
        ]}
        legend={{
          enabled: true,
          align: 'center',
          verticalAlign: 'bottom',
          backgroundColor: 'white',
          borderColor: 'transparent',
          borderWidth: 1,
          shadow: false,
        }}
        xAxis={{
          allowDecimals: false,
          accessibility: {
            rangeDescription: 'Range: 2022 to 2024.',
          },
          labels: {
            formatter: function () {
              const month = String(this.value).split('/')[1]; // Extract month from categories
              const year = String(this.value).split('/')[2]; // Extract year from categories
              return `${month}/${year}`;
            },
          },
          showLastLabel: false,
          tickInterval: 4, // Show one tick per month
          categories: lineChartXData,
        }}
        tooltipFormatter={function () {
          const point: Highcharts.Point = this.point;
          return `<b>${String(point?.category)}</b><br>${point?.series?.name}: ${renderPriceOrDefault(Number(point?.y))}`;
        }}
        plotOptions={{
          area: {
            marker: {
              enabled: false,
              symbol: 'circle',
              radius: 2,
              states: {
                hover: {
                  enabled: true,
                },
              },
            },
          },
        }}
      />
    </StyledWhiteBox>
  );
};

export default LoadDischargeChart;
